/* tanstack */
import { CellContext } from '@tanstack/react-table';

/* Shared */
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { useManagedTableV2Context } from 'application/Shared/Components/Table';
import { Notify, Route } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import {
    ClientLink,
    ClientLinkPutPayload
} from 'application/Shared/types/clientLink';
import { Tenant } from 'application/Shared/types/tenant';
import { ProviderType } from 'application/Shared/types/tenantProvider';
import { usePutClientLink } from 'hooks/dataAccess/ClientLinks/clientLink';

/* hooks */
import { usePutTenant } from 'hooks/dataAccess/Tenant/tenant';
import { usePostTenantSetting } from 'hooks/dataAccess/Tenant/tenantSetting';

/* NPM */
import { TFunction } from 'i18next';

/*  React  */
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

/* Internal */
import { usePartnerClientsTableContext } from './PartnerClientsTableContext';

const enum PartnerClientsTableActions {
    Login = 'login',
    DisableActivityCollection = 'disableActivityCollection',
    RemoveFromPanel = 'removeFromPanel',
    ChangeIntegrationStatus = 'changeIntegrationStatus',
    SetAsInternal = 'setAsInternal',
    UpdateIntegration = 'updateIntegration',
    PermanentlyDelete = 'deleteClient'
}

interface SaveProps {
    clientToEdit: ClientLink;
    blocked?: boolean;
    hidden?: boolean;
    internal?: boolean;
    reloadSessionPartnerTenantLinks: () => void;
    setRerender: Dispatch<SetStateAction<boolean>> | undefined;
    t: TFunction<'translation', undefined>;
    tenant: Tenant | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    putTenant: (params: any) => Promise<boolean>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    postTenantSetting: (params: any) => Promise<boolean>;
    putClientLink: ({
        id,
        params
    }: {
        id: string;
        params: ClientLinkPutPayload;
    }) => Promise<boolean>;
}

const save = async ({
    blocked,
    clientToEdit,
    hidden,
    internal,
    postTenantSetting,
    putClientLink,
    putTenant,
    reloadSessionPartnerTenantLinks,
    setRerender,
    t,
    tenant
}: SaveProps) => {
    const { id, linkedTenantId, platformTenantId } = clientToEdit;
    const { partner } = tenant || {};

    try {
        if (partner) {
            const postResponse = await postTenantSetting({
                key: 'hideInternal',
                value: hidden
            });
            if (postResponse) {
                Notify.s(t('Clients.save.success'));
            } else {
                Notify.e(t('Clients.save.failed'));
            }
        } else {
            const putClientLinkResponse = await putClientLink({
                id,
                params: { hidden: hidden as boolean }
            });
            const putTenantResponse = await putTenant({
                id: linkedTenantId,
                params: {
                    blocked,
                    partnerUpdate: true
                }
            });
            if (internal) {
                const putInternalTenantResponse = await putTenant({
                    id: platformTenantId,
                    params: {
                        internalId: linkedTenantId
                    }
                });
                if (
                    putClientLinkResponse &&
                    putTenantResponse &&
                    putInternalTenantResponse
                ) {
                    Notify.s(t('Clients.save.success'));
                } else {
                    Notify.e(t('Clients.save.failed'));
                }
            } else if (putClientLinkResponse && putTenantResponse) {
                Notify.s(t('Clients.save.success'));
            }
        }

        // If a tenant is hidden/unhidden, reload the partner tenant links
        if (setRerender) {
            setRerender(true);
        }
        reloadSessionPartnerTenantLinks();
    } catch (e) {
        debug.e(e);
        Notify.e(t('Clients.save.failed'));
    }
};

export const Actions = ({ row }: CellContext<ClientLink, unknown>) => {
    const { t } = useTranslation();
    const {
        internalId,
        excludeInternal,
        reloadSessionPartnerTenantLinks,
        setClientToDelete
    } = usePartnerClientsTableContext();
    const { setRerender } = useManagedTableV2Context();
    const navigate = useNavigate();
    const { hidden, platformTenantId, provider, tenant } = row.original;
    const { blocked } = tenant || {};

    const routeToClient = Route.buildBaseAdminRoute({
        clientId: tenant?.id as string,
        partnerId: platformTenantId
    });

    const { putTenant } = usePutTenant();
    const { postTenantSetting } = usePostTenantSetting();
    const { putClientLink } = usePutClientLink();

    // A legacy tenant is one that was BOTH a partner and a client. Originally, partner tenants could monitor themselves,
    // but a new client tenant wasn't created to handle that, they just re-used the partner tenant.
    // Therefore if we would allow to permanentlyDelete a legacy tenant in the clients table we would also be deleting the partner tenant.
    const isLegacyClient = platformTenantId === tenant?.id;

    const options = [
        {
            key: 'login',
            label: t('Common.login'),
            value: PartnerClientsTableActions.Login
        },
        {
            key: 'disableActivityCollection',
            label: blocked
                ? t('Clients.enableActivity')
                : t('Clients.disableActivity'),
            tooltip: blocked
                ? t('Clients.enableActivityDescription')
                : t('Clients.disableActivityDescription'),
            value: PartnerClientsTableActions.DisableActivityCollection
        },
        {
            key: 'removeFromPanel',
            label: hidden
                ? t('Common.addToPanel')
                : t('Common.remove_from_panel'),
            tooltip: hidden
                ? t('Clients.addToPanelDescription')
                : t('Clients.removeFromPanelDescription'),
            value: PartnerClientsTableActions.RemoveFromPanel
        },
        {
            key: 'changeIntegrationStatus',
            label: provider?.find(
                (p) =>
                    p.providerType === ProviderType.Microsoft ||
                    p.providerType === ProviderType.Google
            )?.providerType
                ? t('Clients.updateIntegration')
                : t('Clients.addIntegration'),
            value: PartnerClientsTableActions.UpdateIntegration
        },
        {
            disabled: isLegacyClient,
            key: 'permanentlyDelete',
            label: t('Clients.permanentlyDelete'),
            value: PartnerClientsTableActions.PermanentlyDelete
        }
    ];

    if (!internalId && !excludeInternal) {
        options.push({
            key: 'setAsInternal',
            label: t('Clients.setAsInternal'),
            value: PartnerClientsTableActions.SetAsInternal
        });
    }

    const onClick = (value: PartnerClientsTableActions) => {
        if (value === PartnerClientsTableActions.Login) {
            navigate(routeToClient);
        }
        if (value === PartnerClientsTableActions.DisableActivityCollection) {
            save({
                blocked: !tenant?.blocked,
                clientToEdit: row.original,
                hidden,
                postTenantSetting,
                putClientLink,
                putTenant,
                reloadSessionPartnerTenantLinks,
                setRerender,
                t,
                tenant
            });
        }
        if (value === PartnerClientsTableActions.RemoveFromPanel) {
            save({
                clientToEdit: row.original,
                hidden: !hidden,
                postTenantSetting,
                putClientLink,
                putTenant,
                reloadSessionPartnerTenantLinks,
                setRerender,
                t,
                tenant
            });
        }
        if (value === PartnerClientsTableActions.UpdateIntegration) {
            navigate(`${routeToClient}/identityManagement/configuration`);
        }
        if (value === PartnerClientsTableActions.PermanentlyDelete) {
            setClientToDelete(row.original);
        }
        if (value === PartnerClientsTableActions.SetAsInternal) {
            save({
                clientToEdit: row.original,
                hidden,
                internal: true,
                postTenantSetting,
                putClientLink,
                putTenant,
                reloadSessionPartnerTenantLinks,
                setRerender,
                t,
                tenant
            });
        }
    };

    return (
        <KebabMenu
            options={options}
            onClick={onClick}
            data-testid="partnerClientsTableActions"
        />
    );
};
